import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { queryClient } from '../../lib/queryClient'
import type { Project } from '../../lib/types'
import './tour.css'

// Tour guiado SIN dependencias (docs/brain/domain/tour.md). Vive montado en RequireAuth.
// Arranca por el evento window 'camp:start-tour' (lo dispara "Ayuda y tour" del menú de
// usuario) y AUTO la primera vez (localStorage camp_tour_done ausente) tras 1s en el
// dashboard. Mecánica: overlay oscuro + spotlight sobre el elemento del paso (localizado
// por selector CSS con fallback a paso centrado), tarjeta flotante y navegación entre
// rutas cuando el paso lo pide.

const DONE_KEY = 'camp_tour_done'

type Placement = 'auto' | 'center'

interface Step {
  title: string
  text: string
  // Selector del elemento a iluminar; si no se encuentra → paso centrado sin spotlight.
  selector?: string
  // Ruta a la que navegar al entrar al paso (navegación entre secciones).
  route?: () => string | null
  placement?: Placement
}

// Primer proyecto en cache (qk.projects) para el paso del proyecto. Sin fetch: si no
// hay nada cacheado, el paso cae a modo centrado explicándolo.
function firstCachedProjectId(): string | null {
  const entries = queryClient.getQueriesData<Project[]>({ queryKey: ['projects'] })
  for (const [, list] of entries) {
    const found = (list ?? []).find((p) => p && p.id && p.deletedAt == null)
    if (found) return found.id
  }
  return null
}

const STEPS: Step[] = [
  {
    title: 'Bienvenido a camp',
    text: 'Este es tu espacio de proyectos. Te doy un recorrido de un minuto por lo esencial. Puedes salir cuando quieras con Esc.',
    placement: 'center',
    route: () => '/',
  },
  {
    title: 'El dock de panal',
    text: 'En la esquina inferior izquierda vive el dock de panal: acércate y se despliega hacia la derecha. Ahí están tu inicio, la campanita de avisos, tus favoritos y las secciones de camp.',
    selector: '.bee-dock',
  },
  {
    title: 'Búsqueda global',
    text: 'La lupa de arriba abre el buscador de proyectos: por nombre, descripción, cliente o CUP. Atajo: ⌘K o ⌘B (Ctrl en Windows).',
    selector: '[aria-label="Buscar proyectos"]',
  },
  {
    title: 'Pestañas de navegación',
    text: 'Con el “+” abres pestañas de trabajo. Cada pestaña recuerda su ruta, así saltas entre proyectos sin perder el hilo.',
    selector: '.tab-add',
  },
  {
    title: 'Tu dashboard',
    text: 'Cambia entre vistas, filtra por estatus con los chips y crea con “+ Proyecto”. Hay dos clases: el de cliente, donde eliges cliente de Cells y fecha y el CUP se genera automático, y el interno de la agencia, que va sin cliente ni brief. El filtro de arriba los separa.',
    selector: '.dash-toolbar',
    route: () => '/',
  },
  {
    title: 'El proyecto y sus pestañas',
    text: 'Todo proyecto trae su Overview, más tablero, mensajes, chat, notas y recursos. Los de cliente suman brief, cronograma y las ligas de cotización, scope y presentación en vivo; los internos no los llevan.',
    selector: '.tabbar',
    route: () => {
      const pid = firstCachedProjectId()
      return pid ? `/p/${pid}` : null
    },
  },
  {
    title: 'Tablero tipo Monday',
    text: 'El tablero organiza el trabajo en columnas (agrega las que necesites con “+”) y lo ves como Tabla, Kanban o Planner.',
    selector: '.bt-seg',
  },
  {
    title: 'Chat a la mano',
    text: 'La burbuja hexagonal de abajo a la derecha abre el chat. El Directorio también arranca mensajes directos con cualquiera del equipo.',
    selector: '.chat-hex',
  },
  {
    title: '¡Listo!',
    text: 'Eso es lo esencial. Puedes relanzar este tour cuando quieras desde “Ayuda y tour” en tu menú de usuario. A construir.',
    placement: 'center',
  },
]

interface Rect { top: number; left: number; width: number; height: number }

const PAD = 8 // aire alrededor del elemento iluminado

export function GuidedTour() {
  const navigate = useNavigate()
  const [active, setActive] = useState(false)
  const [step, setStep] = useState(0)
  const [rect, setRect] = useState<Rect | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null)
  const prevFocus = useRef<HTMLElement | null>(null)

  const cur = STEPS[step]!

  const start = useCallback(() => {
    prevFocus.current = document.activeElement as HTMLElement | null
    setStep(0)
    setActive(true)
  }, [])

  const finish = useCallback(() => {
    setActive(false)
    setRect(null)
    setCardPos(null)
    try {
      localStorage.setItem(DONE_KEY, '1')
    } catch {
      // localStorage puede fallar en modo privado: el tour simplemente se relanzará.
    }
    // Devolver el foco a quien abrió el tour (accesibilidad).
    prevFocus.current?.focus?.()
  }, [])

  // Arranque: evento del menú + auto-primera-vez tras cargar el dashboard.
  useEffect(() => {
    const onStart = () => start()
    window.addEventListener('camp:start-tour', onStart)

    let timer: number | undefined
    let done = true
    try {
      done = localStorage.getItem(DONE_KEY) != null
    } catch {
      done = false
    }
    if (!done) timer = window.setTimeout(() => start(), 1000)

    return () => {
      window.removeEventListener('camp:start-tour', onStart)
      if (timer) window.clearTimeout(timer)
    }
  }, [start])

  // Medir el elemento del paso. El elemento puede montar tarde (cambio de ruta,
  // dock que se despliega): reintentamos unas cuantas veces antes de caer a centrado.
  const measure = useCallback((selector?: string): boolean => {
    if (!selector) {
      setRect(null)
      return true
    }
    const el = document.querySelector(selector) as HTMLElement | null
    if (!el) {
      setRect(null)
      return false
    }
    const r = el.getBoundingClientRect()
    if (r.width === 0 && r.height === 0) {
      setRect(null)
      return false
    }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    return true
  }, [])

  // Al entrar a un paso: navegar si toca, luego localizar el objetivo con reintentos.
  useEffect(() => {
    if (!active) return
    const route = cur.route?.()
    if (route) navigate(route)

    let tries = 0
    let raf = 0
    let timer: number | undefined
    const tick = () => {
      const ok = measure(cur.selector)
      if (ok || tries > 12) return
      tries += 1
      timer = window.setTimeout(() => {
        raf = requestAnimationFrame(tick)
      }, 90)
    }
    // Un respiro para que la ruta pinte antes de medir.
    timer = window.setTimeout(() => {
      raf = requestAnimationFrame(tick)
    }, route ? 140 : 0)

    return () => {
      if (raf) cancelAnimationFrame(raf)
      if (timer) window.clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, step])

  // Re-medir en scroll/resize mientras el tour está vivo (el spotlight sigue al elemento).
  useEffect(() => {
    if (!active) return
    const onMove = () => measure(cur.selector)
    window.addEventListener('resize', onMove)
    window.addEventListener('scroll', onMove, true)
    return () => {
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [active, step, cur.selector, measure])

  // Colocar la tarjeta cerca del elemento (o centrada). Se mide la tarjeta real para
  // no salirse de la pantalla.
  useLayoutEffect(() => {
    if (!active) return
    const centered = cur.placement === 'center' || !rect
    if (centered) {
      setCardPos(null)
      return
    }
    const card = cardRef.current
    const cw = card?.offsetWidth ?? 340
    const ch = card?.offsetHeight ?? 180
    const vw = window.innerWidth
    const vh = window.innerHeight
    const gap = 14
    const r = rect!

    // Preferir abajo; si no cabe, arriba; si tampoco, al lado.
    let top: number
    let left = r.left + r.width / 2 - cw / 2
    if (r.top + r.height + gap + ch <= vh) {
      top = r.top + r.height + gap
    } else if (r.top - gap - ch >= 0) {
      top = r.top - gap - ch
    } else {
      // A un costado (dock, burbuja): centra vertical respecto al elemento.
      top = Math.min(Math.max(8, r.top + r.height / 2 - ch / 2), vh - ch - 8)
      if (r.left + r.width / 2 < vw / 2) left = r.left + r.width + gap
      else left = r.left - gap - cw
    }
    left = Math.min(Math.max(8, left), vw - cw - 8)
    top = Math.min(Math.max(8, top), vh - ch - 8)
    setCardPos({ top, left })
  }, [active, rect, step, cur.placement])

  // Teclado: Esc sale; ← / → navegan; foco atrapado en la tarjeta.
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        finish()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setStep((s) => (s < STEPS.length - 1 ? s + 1 : s))
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setStep((s) => (s > 0 ? s - 1 : s))
      } else if (e.key === 'Tab') {
        // Trampa de foco dentro de la tarjeta.
        const card = cardRef.current
        if (!card) return
        const nodes = card.querySelectorAll<HTMLElement>(
          'button, [href], input, [tabindex]:not([tabindex="-1"])',
        )
        if (nodes.length === 0) return
        const first = nodes[0]!
        const last = nodes[nodes.length - 1]!
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [active, finish])

  // Enfocar la tarjeta al cambiar de paso (lector de pantalla + trampa de foco).
  useEffect(() => {
    if (active) cardRef.current?.focus()
  }, [active, step])

  if (!active) return null

  const centered = cur.placement === 'center' || !rect
  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : finish())
  const prev = () => step > 0 && setStep(step - 1)

  return (
    // data-capa-flotante: el tour es una capa propia, así que un clic suyo NO cuenta
    // como "clic fuera" para el panel del chat (lib/useCerrarAlClicFuera.ts).
    <div className="tour-root" role="presentation" data-capa-flotante>
      {/* Backdrop oscuro. Con spotlight el recorte lo hace el elemento .tour-spot. */}
      {centered ? (
        <div className="tour-backdrop" onClick={finish} />
      ) : (
        <div
          className="tour-spot"
          style={{
            top: rect!.top - PAD,
            left: rect!.left - PAD,
            width: rect!.width + PAD * 2,
            height: rect!.height + PAD * 2,
          }}
        />
      )}

      <div
        ref={cardRef}
        className={centered ? 'tour-card tour-card-center' : 'tour-card'}
        style={centered || !cardPos ? undefined : { top: cardPos.top, left: cardPos.left }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-text"
        tabIndex={-1}
      >
        <div className="tour-step-count">Paso {step + 1} de {STEPS.length}</div>
        <h3 className="tour-title" id="tour-title">{cur.title}</h3>
        <p className="tour-text" id="tour-text">{cur.text}</p>

        <div className="tour-dots" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span key={i} className={i === step ? 'tour-dot on' : 'tour-dot'} />
          ))}
        </div>

        <div className="tour-actions">
          <button type="button" className="btn btn-ghost tour-exit" onClick={finish}>
            Salir
          </button>
          <span className="spacer" />
          {step > 0 && (
            <button type="button" className="btn btn-ghost" onClick={prev}>
              Anterior
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={next}>
            {step < STEPS.length - 1 ? 'Siguiente' : 'Terminar'}
          </button>
        </div>
      </div>
    </div>
  )
}
